import { Form } from 'antd';
import { HeaderContent, LoadingFallback, ManageProductComponent, showAlertNotification } from 'components/common';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateProductVersionMutation, useGetProductByIdQuery, useUpdateProductVersionMutation } from 'services';
import { Attribute, ImagePayload, ProductPayload, ProductSalePrice, Unit } from 'types';
import { omit } from 'utils';
import { initializeFormData } from 'utils/productHelpers';

const DetailProductPage: React.FC = () => {
  const { productId, versionId } = useParams();
  const [versionSelect, setVersionSelect] = useState<string>(versionId || '');
  const [form] = Form.useForm();
  const [updateProduct, { isLoading: isLoadingUpdate }] = useUpdateProductVersionMutation();
  const [createProductVertion, { isLoading: isLoadingProductVertion }] = useCreateProductVersionMutation();
  const [initialProductUnits, setInitialProductUnits] = useState<Unit[]>([]);
  const [initialProductAttributes, setInitialProductAttributes] = useState<Attribute[]>([]);
  const [initialProductSalePrices, setInitialProductSalePrices] = useState<ProductSalePrice[]>([]);
  const [initalProductSuppliers, setInitialProductSuppliers] = useState<{ supplierId: string; id?: string }[]>([]);
  const navigate = useNavigate();
  const payloadRef = useRef<ProductPayload | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    data,
    refetch,
    isLoading: isLoadingGet,
    isSuccess,
  } = useGetProductByIdQuery(
    {
      productId: productId || '',
      versionId: versionSelect,
      query: {
        include:
          'product.productVersions|productAttributes.attribute|productUnits.unit|status|productImages.image|currencyUnit|baseUnit|productSalePrices|brand|category|product.productSuppliers.supplier',
      },
    },
    {
      skip: !productId || !versionSelect,
    },
  );

  const choiceVersion = (version: string) => {
    setVersionSelect(version);
  };
  const handleSubmitForm = async (
    {
      productUnits,
      productAttributes,
      imagesData,
      productSalePrices,
      productSuppliers,
    }: {
      productUnits: Unit[];
      productAttributes: Attribute[];
      imagesData: ImagePayload[];
      productSalePrices?: ProductSalePrice[];
      productSuppliers?: string[];
    },
    isPublish: boolean = false,
    publishStatusId?: string,
    isActive: boolean = false,
    setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
    setStatusSave?: React.Dispatch<React.SetStateAction<string>>,
    isUpdateVersion?: boolean,
    version?: number,
    isProductActive?: boolean,
  ) => {
    try {
      if (isPublish) {
        setStatusSave?.('bản công khai');
        await form.validateFields();
      } else {
        setStatusSave?.('bản nháp');
      }

      productUnits = productUnits
        .filter((unit) => unit.checked === true)
        .map((unit) => {
          if (unit.isNew) {
            unit.unitId = null;
            unit.name = unit.label;
            unit = omit(unit, ['unitId', 'isNew']);
          }
          const cleaned = omit(unit, ['checked', 'label']);

          return cleaned;
        });

      productAttributes = productAttributes.map((attr) => omit(attr, ['surcharge', 'title']));
      const values = await form.getFieldsValue();

      if (isUpdateVersion) {
        const payload: ProductPayload = {
          code: values.code,
          name: values.name,
          categoryId: values.category,
          statusId: publishStatusId,
          dimension: values.dimension,
          color: values.color,
          sizeSpec: values.sizeSpec,
          baseUnitId: values.baseUnitId,
          costPrice: Number(values.costPrice),
          salePrice: Number(values.salePrice),
          description: values.describeProduct,
          brandId: values.brand,
          currencyUnitId: values.currencyUnitId,
          minQuantity: Number(values.min),
          maxQuantity: Number(values.max),
          density: Number(values.density),
          productAttributes: productAttributes.map((attr) => {
            return {
              attributeId: attr.attributeId,
              salePrice: attr.salePrice || 0,
              note: attr.note,
              order: attr.order,
            } as Attribute;
          }),
          productUnits: productUnits.map((unit) => {
            return {
              unitId: unit.unitId,
              convertValue: unit.convertValue,
              order: unit.order,
              costPrice: unit.costPrice,
              salePrice: unit.salePrice,
            };
          }),
          productSalePrices: productSalePrices?.map((salePrice) => ({
            maxQuantity: salePrice.maxQuantity,
            minQuantity: salePrice.minQuantity,
            salePrice: Number(salePrice.salePrice),
            note: salePrice.note,
          })),
          productSuppliers: productSuppliers?.map((supplier) => {
            const initialSupplier = initalProductSuppliers.find((s) => s.supplierId === supplier);
            return {
              supplierId: supplier,
              costPrice: Number(values.costPrice),
              version: version,
              id: initialSupplier?.id,
            };
          }),
          imagesInfo: imagesData.map(({ filePath, name, order }) => ({ filePath, name, order })),
          isPublishing: isPublish,
          isActive: isActive,
          version: version,
          isUpdateVersion: isUpdateVersion,
        };
        payloadRef.current = payload;
        setModalOpen?.(true);
        form.setFieldValue('status', isPublish ? 'Inactive' : '');
      } else {
        const payload: ProductPayload = {
          code: values.code,
          name: values.name,
          categoryId: values.category,
          statusId: publishStatusId,
          costPrice: Number(values.costPrice),
          salePrice: Number(values.salePrice),
          description: values.describeProduct,
          baseUnitId: values.baseUnitId,
          brandId: values.brand,
          dimension: values.dimension,
          color: values.color,
          sizeSpec: values.sizeSpec,
          currencyUnitId: values.currencyUnitId,
          minQuantity: Number(values.min),
          maxQuantity: Number(values.max),
          density: Number(values.density),
          barcode: values.barcode,
          productAttributes,
          productUnits,
          productSalePrices: productSalePrices?.map((salePrice) => ({
            ...salePrice,
            salePrice: Number(salePrice.salePrice),
          })),
          productSuppliers: productSuppliers?.map((supplier) => ({
            supplierId: supplier,
            costPrice: Number(values.costPrice),
            version: version,
          })),
          imagesInfo: imagesData.map(({ filePath, name, order }) => ({ filePath, name, order })),
          isPublishing: isPublish,
          isActive: values.isActive,
          version: version,
          isProductActive: isProductActive,
        };
        payloadRef.current = payload;
        setModalOpen?.(true);
        form.setFieldValue('status', isPublish ? 'Inactive' : '');
      }
    } catch (err) {
      const error = err as { data?: { statusCode?: number } };

      console.error('Validation failed:', error);

      if (error.data?.statusCode === 400) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: `Sản phẩm không hoạt động hoặc đã ngừng kinh doanh.`,
        });
      } else {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: `Vui lòng nhập đúng thông tin yêu cầu.`,
        });
      }
    }
  };
  useEffect(() => {
    if (isSuccess && data?.data) {
      initializeFormData(form, data?.data);

      if (data.data.productUnits && Array.isArray(data.data.productUnits)) {
        setInitialProductUnits(data.data.productUnits);
      }

      if (data.data.productAttributes && Array.isArray(data.data.productAttributes)) {
        setInitialProductAttributes(data.data.productAttributes);
      }

      if (data.data.productSalePrices && Array.isArray(data.data.productSalePrices)) {
        setInitialProductSalePrices(data.data.productSalePrices);
      }
      if (data.data.product?.productSuppliers && Array.isArray(data.data.product?.productSuppliers)) {
        setInitialProductSuppliers(
          data.data.product?.productSuppliers?.map((supplier) => ({
            supplierId: supplier.supplierId,
            id: supplier.id,
          })) || [],
        );
      }
      setIsInitialized(true);
    }
  }, [isSuccess, data, form]);
  useEffect(() => {
    if (!versionSelect) return;

    const fetchData = async () => {
      setIsInitialized(false);
      const result = await refetch();
      if (result?.data?.data) {
        initializeFormData(form, result.data.data);

        if (result.data.data.productUnits && Array.isArray(result.data.data.productUnits)) {
          setInitialProductUnits(result.data.data.productUnits);
        }

        if (result.data.data.productAttributes && Array.isArray(result.data.data.productAttributes)) {
          setInitialProductAttributes(result.data.data.productAttributes);
        }

        if (result.data.data.productSalePrices && Array.isArray(result.data.data.productSalePrices)) {
          setInitialProductSalePrices(
            result.data.data.productSalePrices.map((salePrice) => ({
              ...salePrice,
              salePrice: Number(salePrice.salePrice),
            })),
          );
        }
        if (result.data.data.product?.productSuppliers && Array.isArray(result.data.data.product?.productSuppliers)) {
          setInitialProductSuppliers(
            result.data.data.product?.productSuppliers?.map((supplier) => ({
              supplierId: supplier.supplierId,
              id: supplier.id,
            })) || [],
          );
        }
      }
      setIsInitialized(true);
    };

    fetchData();
  }, [versionSelect, refetch, form]);
  const handleConfirmUpdate = async (setInactive?: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (!payloadRef.current) return;
    try {
      if (payloadRef.current.isUpdateVersion) {
        const res = await createProductVertion({
          productId: productId || '',
          data: payloadRef.current,
        }).unwrap();
        if (res.success) {
          setVersionSelect(res?.data?.id || '');
          form.resetFields();
          showAlertNotification({
            type: 'success',
            title: 'Nâng cấp phiên bản thành công',
            message: `Sản phẩm nâng cấp có mã: ${res?.data?.product?.code} và phiện bản là Ver ${res?.data?.version?.toFixed(1)}.`,
          });
          navigate('/items/products?newChange=true');
        }
      } else {
        const res = await updateProduct({
          productId: productId || '',
          versionId: versionSelect,
          data: payloadRef.current,
        }).unwrap();
        if (res.success) {
          form.resetFields();
          if (payloadRef.current.isProductActive) {
            showAlertNotification({
              type: 'success',
              title: 'Ngừng kinh doanh sản phẩm thành công',
              message: `Bạn đã ngừng kinh doanh sản phẩm có mã: ${res?.data?.product?.code} được tô nền màu xanh trên đầu bảng.`,
            });
            refetch();
          } else {
            if (payloadRef.current.isPublishing) {
              console.log('res?.data', res?.data);
              refetch();
              showAlertNotification({
                type: 'success',
                title: 'Công khai sản phẩm thành công',
                message: `Sản phẩm bạn vừa công khai có mã: ${res?.data?.product?.code} được tô nền màu xanh trên đầu bảng.`,
              });
            } else {
              showAlertNotification({
                type: 'success',
                title: 'Cập nhập sản phẩm thành công',
                message: `Sản phẩm bạn vừa cập nhập có mã: ${res?.data?.product?.code} được tô nền màu xanh trên đầu bảng.`,
              });
            }
            payloadRef.current = null;
          }
          // clear state
          form.resetFields();
          navigate('/items/products?newChange=true');
        }
      }
    } catch {
      showAlertNotification({
        type: 'error',
        title: 'Lỗi Nâng Cấp',
        message: `Vui lòng nhập đúng thông tin yêu cầu.`,
      });
      setInactive?.(false);
      if (data?.data) {
        initializeFormData(form, data.data as ProductPayload);
      }
    }
  };
  const handleCancelUpdate = async (setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>) => {
    setModalOpen?.(false);
    payloadRef.current = null;
  };
  const handleReplaceVersion = async (versionReplace: string, version: number) => {
    const res = await updateProduct({
      productId: productId || '',
      versionId: versionReplace,
      data: {
        isActive: true,
      },
    }).unwrap();
    if (res.success) {
      refetch();
      form.setFieldValue('isActive', false);
      showAlertNotification({
        type: 'warning',
        title: 'Cảnh báo',
        message: `Bạn vừa hủy kích hoạt phiên bản này, phiên bản Ver ${version} đã được kích hoạt để thay thế.`,
      });
    }
  };
  const handleActive = async (
    {
      productUnits,
      productAttributes,
      imagesData,
      productSalePrices,
      productSuppliers,
    }: {
      productUnits: Unit[];
      productAttributes: Attribute[];
      imagesData: ImagePayload[];
      productSalePrices?: ProductSalePrice[];
      productSuppliers?: string[];
    },
    isActive: boolean,
  ) => {
    const values = await form.getFieldsValue();
    productUnits = productUnits.filter((unit) => unit.checked === true).map((unit) => omit(unit, ['checked', 'label']));
    productAttributes = productAttributes.map((attr) => omit(attr, ['surcharge', 'title']));
    const version = form.getFieldValue('version');
    const res = await updateProduct({
      productId: productId || '',
      versionId: versionSelect,
      data: {
        code: values.code,
        name: values.name,
        categoryId: values.category,
        costPrice: Number(values.costPrice),
        salePrice: Number(values.salePrice),
        description: values.describeProduct,
        baseUnitId: values.baseUnitId,
        dimension: values.dimension,
        color: values.color,
        sizeSpec: values.sizeSpec,
        density: Number(values.density),
        brandId: values.brand,
        currencyUnitId: values.currency,
        minQuantity: Number(values.min),
        maxQuantity: Number(values.max),
        productAttributes,
        productUnits,
        productSalePrices: productSalePrices?.map((salePrice) => ({
          ...salePrice,
          salePrice: Number(salePrice.salePrice),
        })),
        productSuppliers: productSuppliers?.map((supplier) => ({
          supplierId: supplier,
          costPrice: Number(values.costPrice),
          version: version,
        })),
        version: version,
        imagesInfo: imagesData.map(({ filePath, name, order }) => ({ filePath, name, order })),
        isActive: isActive,
      },
    }).unwrap();
    if (res.success) {
      refetch();
      form.setFieldValue('isActive', isActive);
      showAlertNotification({
        type: `${isActive ? 'success' : 'warning'}`,
        title: `${isActive ? 'Kích hoạt phiên bản thành công' : 'Cảnh báo'}`,
        message: `${isActive ? `Phiên bản bạn kích hoạt là : ${res?.data?.version}.` : `Bạn vừa hủy kích hoạt phiên bản này và không thay thế, sản phẩm sẽ không được kích hoạt`}`,
      });
    }
  };
  if (isLoadingGet || isLoadingUpdate || isLoadingProductVertion || !isInitialized) {
    return <LoadingFallback />;
  }
  return (
    <>
      <HeaderContent
        size='sm'
        key='confirm-create-product'
        label={'Chi Tiết Sản Phẩm'}
        contentHeader={'Bây giờ, bạn hãy điền toàn bộ các thông tin cơ bản bên dưới để hoàn thành một sản phẩm'}
      />
      <ManageProductComponent
        form={form}
        isLoading={isLoadingGet || isLoadingUpdate || isLoadingProductVertion}
        onSubmit={handleSubmitForm}
        defaultIsEdit={false}
        isDetail={true}
        choiceVersion={choiceVersion}
        onCancel={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        onActive={handleActive}
        onReplaceVersion={handleReplaceVersion}
        initialProductUnits={initialProductUnits}
        initialProductAttributes={initialProductAttributes}
        initialProductSalePrices={initialProductSalePrices}
        initialProductSuppliers={initalProductSuppliers.map((supplier) => supplier.supplierId)}
      />
    </>
  );
};

export default DetailProductPage;
